import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authService } from "@/services/auth.service";
import {
    getStoredToken,
    getStoredUser,
    setStoredToken,
    setStoredUser,
} from "@/services/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(() => getStoredUser());
    const [token, setToken] = useState(() => getStoredToken());
    const [loading, setLoading] = useState(!!getStoredToken());

    // Refresh user profile whenever we have a token
    useEffect(() => {
        let cancelled = false;
        async function hydrate() {
            if (!token) {
                setLoading(false);
                return;
            }
            try {
                const me = await authService.me();
                if (!cancelled) {
                    setUser(me);
                    setStoredUser(me);
                }
            } catch {
                if (!cancelled) {
                    setStoredToken(null);
                    setStoredUser(null);
                    setUser(null);
                    setToken(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        hydrate();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const login = useCallback(async (email, password) => {
        const res = await authService.login(email, password);
        setStoredToken(res.access_token);
        setStoredUser(res.user);
        setToken(res.access_token);
        setUser(res.user);
        return res.user;
    }, []);

    const logout = useCallback(() => {
        setStoredToken(null);
        setStoredUser(null);
        setToken(null);
        setUser(null);
    }, []);

    const refresh = useCallback(async () => {
        try {
            const me = await authService.me();
            setUser(me);
            setStoredUser(me);
            return me;
        } catch (err) {
            // Silent failures are expected (e.g., expired token → 401 handled
            // by the axios interceptor). Log at debug level for diagnostics.
            // eslint-disable-next-line no-console
            console.debug("[AuthContext] refresh() failed:", err?.message || err);
            return null;
        }
    }, []);

    const value = useMemo(
        () => ({ user, token, loading, login, logout, refresh, isAuthenticated: !!token }),
        [user, token, loading, login, logout, refresh],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth deve ser usado dentro de AuthProvider");
    return ctx;
}
